import {GROUND_BACKGROUNDS} from './groundBackgrounds';

/**
 * АНТУРАЖ ПОЛЯ — стаи птиц и облака над пейзажем, орлы над горами: на поле мы
 * смотрим с большой высоты, как на карту мира (см. scenes/FieldAmbience).
 *
 * Антураж целиком визуальный: в состояние партии он не попадает, у каждого
 * клиента свой, на правила и записи матчей не влияет.
 *
 * Здесь — только числа и перечни. Вся случайность и геометрия живут в
 * utils/effects/buildBirdFlock, utils/effects/buildCloudField и
 * utils/effects/buildEagleFlight. Птица стаи рисуется кодом
 * (utils/effects/fieldAmbienceTextures), орел — картинка владельца игры
 * (FIELD_EAGLE_IMAGE), облака — готовый лист картинок (см. FIELD_CLOUDS_SHEET).
 *
 * ОТКЛЮЧИТЬ ОБЛАКА ИЛИ ПТИЦ НА ПОДЛОЖКЕ — см. AMBIENCE_BY_GROUND_BACKGROUND в
 * конце файла: солнечным картам (пустыни, соль) облака не положены. Где нет
 * птиц, нет и орлов.
 */

// Текстура птицы: картинки в public нет, пиксели считает код
export const FIELD_BIRD_TEXTURE_KEY = 'ambience_bird';
// Запасная текстура орла — тоже из кода (бурый, с белой головой): ею орел
// рисуется, только если его картинка не загрузилась
export const FIELD_EAGLE_TEXTURE_KEY = 'ambience_eagle';

/**
 * Картинка орла: вид сверху, голова вправо, крылья по оси Y. Арт владельца
 * игры, подготовленный скриптом
 * developer_artifacts/scripts/generateFieldAmbienceBirds.cjs (исходник — в
 * developer_artifacts/art_sources/field_ambience/birds): путь и размеры
 * вписывает он, как и загрузку картинки в GameScene.preload(). wingspan —
 * размах крыльев в точках картинки.
 */
export const FIELD_EAGLE_IMAGE = Object.freeze({
  key: 'ambience_eagle_image',
  path: '/img/effects/other/eagle.3d6607db.png',
  width: 71,
  height: 177,
  wingspan: 176,
});

/**
 * Лист облаков: кадры идут подряд, рядами. Сначала все облака (вид сверху),
 * сразу за ними их тени — размытые силуэты тех же облаков в том же порядке
 * (кадр тени = frameCount + кадр облака). Картинок больше, чем облаков над
 * пейзажем разом (FIELD_CLOUDS.count): одинаковые рядом не встречаются. Только
 * в зимнюю вьюгу облаков больше, чем картинок (FIELD_CLOUDS.storm): тогда
 * картинки повторяются поровну.
 *
 * Лист собирает скрипт developer_artifacts/scripts/generateFieldAmbienceSprites.cjs —
 * из картинок в developer_artifacts/art_sources/field_ambience, а если их нет,
 * рисует облака сам. Он же вписывает сюда путь и число кадров: в имени файла
 * стоит отпечаток содержимого, чтобы обновленный лист не доставался из кеша
 * браузера (картинки отдаются с кешем на сутки, а раскладка кадров зашита в
 * код — старый лист с новым кодом показал бы тени вместо облаков). Руками путь
 * и число кадров не правят. Ключ, путь и размер кадра должны совпадать с
 * загрузкой в GameScene.preload() — сверяет тест конфига
 */
export const FIELD_CLOUDS_SHEET = {
  key: 'ambience_clouds',
  path: '/img/effects/ambience/clouds.4606e19f.png',
  frameWidth: 384,
  frameHeight: 240,
  frameCount: 15,
};

/**
 * Слои антуража среди слоев поля (см. LAYER_DEPTH в HexGridRenderer): выше
 * отрядов с их значками (до 4.0) и искр попаданий (4.6), но ниже подписей
 * дохода (4.9), значков и кнопок ячеек (5 и выше). Птицы и орлы летят ниже
 * облаков
 */
export const FIELD_AMBIENCE_DEPTH = {
  CLOUD_SHADOW: 4.7,
  BIRD: 4.75,
  EAGLE: 4.76,
  CLOUD: 4.8,
};

// Длиннее этого шаг времени не бывает: после долгого кадра антураж не прыгает
export const FIELD_AMBIENCE_MAX_STEP_MS = 250;

export const FIELD_BIRDS = {
  // Первая стая появляется вскоре после начала, следующие — после паузы,
  // которая отсчитывается от ухода предыдущей: двух стай разом не бывает
  firstFlockDelayMs: {min: 5000, max: 14000},
  flockIntervalMs: {min: 25000, max: 70000},
  // Размах крыльев, доля размера ячейки: птицы — едва различимые черточки
  wingspanFactor: 0.27,
  // Скорость стаи, размеров ячейки в секунду
  speedFactor: {min: 2.4, max: 3.4},
  // Наклон курса к горизонтали: стая летит от бокового края к боковому, но не
  // строго по линейке
  headingTiltDeg: 12,
  // Курс стаи проходит над полем или рядом с ним: не дальше стольких размеров
  // ячейки выше или ниже него
  laneFieldMarginFactor: 3,
  // Полоса сверху и снизу пейзажа, в которую курс не заходит никогда (доля
  // высоты): картинка там уходит в черный
  laneMarginShare: 0.14,
  // Вся стая плавно гуляет поперек курса (доля размера ячейки)
  swayFactor: 0.3,
  swayPeriodMs: {min: 5000, max: 9000},
  // Взмах: на сколько укорачивается размах крыльев в нижней точке
  flapDepth: 0.55,
  alpha: 0.9,
  // Цвета стай: каждая стая — одного цвета, он выбирается при ее появлении
  colors: {white: 0xf4f2ea, black: 0x1f1d1b, brown: 0x6b4526},
  formations: {
    // Клин: неторопливые взмахи, строй почти не шевелится
    wedge: {
      weight: 1,
      birdCount: {min: 5, max: 11},
      // Расстояние между соседями вдоль плеча клина, доля размера ячейки
      spacingFactor: 0.42,
      // Угол плеча к курсу
      armAngleDeg: 34,
      // Какая доля птиц (кроме вожака) летит в первом плече
      armShare: {min: 0.35, max: 0.65},
      // Неровность строя, доля размера ячейки
      jitterFactor: 0.05,
      sizeFactor: {min: 0.92, max: 1.08},
      flapPeriodMs: {min: 560, max: 700},
      wanderFactor: 0.05,
      wanderPeriodMs: {min: 2600, max: 4200},
    },
    // Стайка: мелкие птицы, частые взмахи, пятно все время меняет очертания
    swarm: {
      weight: 1,
      birdCount: {min: 9, max: 20},
      // Радиус пятна, доля размера ячейки; вдоль курса пятно вытянуто
      radiusFactor: {min: 0.8, max: 1.5},
      stretch: 1.5,
      sizeFactor: {min: 0.68, max: 0.95},
      flapPeriodMs: {min: 300, max: 420},
      wanderFactor: 0.3,
      wanderPeriodMs: {min: 1800, max: 3600},
    },
  },
};

/**
 * ОРЕЛ НАД ГОРОЙ. Изредка над жетоном горы появляется орел: проступает из
 * вышины, делает несколько кругов и уходит обратно. Гора выбирается случайно.
 *
 * Чтобы два орла над разными горами не кружили как под копирку, у полета есть
 * несколько видов пути (paths), а у каждого полета — свои радиус, наклон,
 * сторона обхода, начальная точка и темп. Второй орел, появившийся при
 * первом, берет другой вид пути.
 */
export const FIELD_EAGLES = {
  // Первый орел — вскоре после начала; следующий — через паузу, которая
  // отсчитывается от появления предыдущего. Орел — редкий гость: пауза в
  // среднем около 2.7 минуты (КАК ЧАСТО — это intervalMs; вместе с орлом реже
  // звучит и его клекот). Нижняя граница чуть короче самого долгого полета,
  // так что два орла разом возможны, но при такой паузе почти не случаются
  firstDelayMs: {min: 6000, max: 20000},
  intervalMs: {min: 20000, max: 300000},
  // Сколько орлов кружит разом, не больше; над одной горой — один
  maxAtOnce: 2,
  // Размах крыльев, доля размера ячейки: орел вдвое крупнее птицы из стаи
  wingspanFactor: FIELD_BIRDS.wingspanFactor * 2,
  sizeFactor: {min: 0.94, max: 1.08},
  alpha: 0.96,
  // Сколько орел кружит над горой и за сколько проступает и исчезает
  durationMs: {min: 14000, max: 24000},
  fadeMs: 1800,
  // Время одного оборота
  loopMs: {min: 6400, max: 9200},
  // Радиус круга, доля размера ячейки: орел кружит над самой горой
  radiusFactor: {min: 0.4, max: 0.68},
  // Центр круга — не точно над серединой жетона (доля размера ячейки)
  centerJitterFactor: 0.14,
  // Орел парит: крылья неподвижны, раз в несколько секунд — пара неторопливых
  // взмахов (на сколько укорачивается размах, длительность взмаха, их число)
  flap: {depth: 0.28, periodMs: 640, count: 2, everyMs: {min: 3600, max: 6200}},
  // Виды пути вокруг горы. ratio — отношение малой оси к большой; scale —
  // множитель радиуса; wobble — насколько радиус «дышит» за оборот
  paths: {
    // Ровный круг
    circle: {weight: 1},
    // Вытянутый овал, повернутый как придется
    ellipse: {weight: 1, ratio: {min: 0.55, max: 0.78}, scale: 1.15},
    // Восьмерка: два витка в разные стороны
    eight: {weight: 1, ratio: {min: 0.5, max: 0.7}, scale: 1.3},
    // Петля, которая с каждым оборотом смещается: круг то шире, то уже
    rosette: {weight: 1, wobble: {min: 0.2, max: 0.34}},
  },
};

export const FIELD_CLOUDS = {
  // Сколько облаков над пейзажем разом (в зимнюю вьюгу — больше, см. storm)
  count: 12,
  // Ширина кадра облака, доля стороны пейзажа: пейзаж масштабируется вместе с
  // полем, и облака остаются соразмерны горам и лесам на нем...
  widthShare: {min: 0.08, max: 0.2},
  // ...но не шире стольких размеров ячейки: на огромном поле облако во весь
  // экран вышло бы мутным пятном и закрывало бы десятки ячеек
  maxWidthFactor: 9,
  // Высота кадра облака к его ширине — как у кадра листа
  aspect: FIELD_CLOUDS_SHEET.frameHeight / FIELD_CLOUDS_SHEET.frameWidth,
  // За сколько секунд облако пересекает пейзаж от края до края...
  crossingSeconds: 170,
  // ...но не быстрее стольких размеров ячейки в секунду (огромное поле)
  maxSpeedFactor: 0.6,
  // Разброс скоростей: все облака плывут почти одинаково
  speedSpreadShare: 0.1,
  // Наклон ветра к горизонтали, не больше (градусы)
  driftTiltDeg: 7,
  // Случайный поворот картинки облака
  rotationDeg: 10,
  alpha: {min: 0.72, max: 0.92},
  // Тень — размытый черный силуэт облака: чуть ниже него и правее (свет на
  // пейзажах падает слева сверху)
  shadow: {
    alpha: 0.36,
    // Смещение от облака, доля ширины его кадра
    offsetShare: {x: 0.08, y: 0.27},
  },
  // Сколько мест перебирается для нового облака (берется самое просторное)
  respawnAttempts: 3,
  // У края картинки пейзажа облако проявляется постепенно: видимость 0 на
  // расстоянии fromShare от края и 1 — на toShare (доли стороны пейзажа;
  // сама картинка уходит в черный на последних 12%)
  edgeFade: {fromShare: 0.03, toShare: 0.15},
  /**
   * НАД ИГРОВЫМ ПОЛЕМ ОБЛАКА ВИДНЫ, но прозрачнее, чем над пейзажем: отряды и
   * строения под ними должны читаться.
   *  - alphaFactor — во сколько раз облако и его тень прозрачнее над полем
   *    (1 — такие же плотные, как над пейзажем; 0 — над полем не видны вовсе);
   *  - gridStepFactor и depthFactor — шаг и глубина перехода карты покрытия
   *    поля, в размерах ячейки (см. buildFieldCoverage);
   *  - from и to — значения карты покрытия (0 — далеко от поля, 0.5 — кромка
   *    поля, 1 — его глубина), между которыми облако светлеет. Покрытие
   *    берется по всему пятну облака, а не по центру: облако начинает
   *    светлеть, когда его край только подходит к полю;
   *  - reachShare — полуразмеры пятна облака в долях ширины кадра
   */
  overField: {
    alphaFactor: 0.8,
    gridStepFactor: 0.5,
    depthFactor: 2.6,
    from: 0.25,
    to: 0.62,
    reachShare: {x: 0.3, y: 0.18},
  },
  /**
   * ВЬЮГА ГОНИТ ОБЛАКА (зимние подложки; сама вьюга — configs/biomeEffects/snowDrift,
   * utils/effects/biome/snowSquall). Просьба владельца игры: когда налетает
   * снежный заряд, облака разгоняются, и их становится в 2–3 раза больше.
   * Разгон идет за силой заряда — той же, по которой нарастают и стихают его
   * снег и вой, — а лишние облака прибывают все время заряда, поровну:
   *  - speedBlendShare — до какой скорости заряд разгоняет облако: доля пути
   *    от его обычной скорости до скорости потока снега (ее сообщает сам
   *    заряд: здесь этого числа нет). 0 — облака плывут как плыли, 1 — несутся
   *    со снегом, 0.5 — ровно посередине. Сначала облака разгонялись до
   *    скорости снега; владелец игры посмотрел и попросил середину между
   *    обычной скоростью облаков и скоростью частиц вьюги: со снегом наравне
   *    облака летят уж слишком быстро;
   *  - countFactor — на сколько облаков рассчитан поток облаков вьюги: во
   *    столько раз облаков над пейзажем больше обычного (count), когда поток
   *    дошел до подветренного края. Лишние приходят из-за наветренного края,
   *    как любое вернувшееся облако, — по одному, через равные промежутки,
   *    пока заряд налетает и держится. Промежуток не задан, а считается: за
   *    время, за которое облако на скорости вьюги пересекает пейзаж, приходят
   *    все лишние облака. Раньше лишние набегали все разом, пока заряд
   *    налетал, и владелец игры увидел в начале вьюги толпу облаков, а посреди
   *    нее — куда меньше: попросил, чтобы облака появлялись равномерно, все
   *    время вьюги. На обычном поле голова потока доходит до подветренного
   *    края секунд через двадцать пять — заряд к этому времени уже стихает
   *    (держится он 20–25 секунд от начала): облаков над пейзажем прибывает
   *    до самого спада, и набирается их от трех четвертей расчетного за
   *    самый короткий заряд до всех за самый долгий. Заряд стихает — новые
   *    не приходят, а пришедшие тают вместе с ним: медленными облаками они
   *    висели бы над пейзажем еще минуты;
   *  - entryJitterShare — промежуток между приходами облаков вьюги гуляет на
   *    такую долю: через ровные промежутки облака шли бы как по метроному;
   *  - rampMs — быстрее, чем за столько, вьюга облаками не завладевает и их
   *    не отпускает (от обычного хода до полного разгона и обратно). Заряд
   *    могут убрать разом (сбой эффекта подложки), и облака не должны встать
   *    рывком. Сам заряд налетает и стихает за 7–11 секунд, не круче этого
   *    предела: пока он идет своим чередом, предел ничего не меняет
   */
  storm: {
    speedBlendShare: 0.5,
    countFactor: 2.5,
    entryJitterShare: 0.25,
    rampMs: 4000,
  },
};

const BIRD_COLORS = FIELD_BIRDS.colors;
/**
 * Птицы подложки: colors — какого цвета бывают стаи (каждая — одного, цвет
 * выбирается случайно при ее появлении). По умолчанию — любого из трех; на
 * подложке, где какой-то цвет не разглядеть (белые на снегу, черные на
 * вулканическом камне), его в перечне нет
 */
const createBirds = (colors) => Object.freeze({isEnabled: true, colors: Object.freeze([...colors])});
const BIRDS_ANY = createBirds([BIRD_COLORS.white, BIRD_COLORS.black, BIRD_COLORS.brown]);
const BIRDS_DARK = createBirds([BIRD_COLORS.black, BIRD_COLORS.brown]);
const BIRDS_WHITE = createBirds([BIRD_COLORS.white]);
const BIRDS_OFF = Object.freeze({isEnabled: false});

/**
 * Облака подложки: tint — цвет, в который красится картинка облака;
 * alphaFactor — множитель плотности облака; shadowAlphaFactor — множитель
 * плотности тени (0 — тени нет); isThinnerOverField — становится ли облако
 * прозрачнее над игровым полем (см. FIELD_CLOUDS.overField)
 */
const createClouds = ({
  tint = 0xffffff, alphaFactor = 1, shadowAlphaFactor = 1, isThinnerOverField = true,
} = {}) => Object.freeze({
  isEnabled: true, tint, alphaFactor, hasShadow: shadowAlphaFactor > 0, shadowAlphaFactor, isThinnerOverField,
});
const CLOUDS_WHITE = createClouds();
// На темной зелени и воде тень той же плотности, что на снегу, едва видна
const CLOUDS_WHITE_OVER_DARK = createClouds({shadowAlphaFactor: 1.35});
const CLOUDS_OFF = Object.freeze({isEnabled: false});
// Дымка на темных подложках: белое облако там смотрится чужим, а тень на
// темной земле все равно не видна. Над полем дымка прозрачнее НЕ становится
// (решение владельца игры): она и так вдвое бледнее белого облака, и над
// ячейками ее не было видно вовсе
const createHaze = (tint, alphaFactor) => (
  createClouds({tint, alphaFactor, shadowAlphaFactor: 0, isThinnerOverField: false})
);

// Подложка, которой в перечне ниже нет (новую завели, а антураж ей не
// назначили): белые облака с тенью и стаи любого цвета
const DEFAULT_AMBIENCE = Object.freeze({birds: BIRDS_ANY, clouds: CLOUDS_WHITE});

/**
 * Антураж у подложки поля: идентификатор подложки (см. configs/groundBackgrounds)
 * -> птицы и облака.
 *
 * ОБЛАКА ОТКЛЮЧЕНЫ на солнечных подложках — пустыни, соль, красный каньон,
 * кости, песчаные спирали: над ними ясное небо. Чтобы отключить облака еще
 * где-то, поставь подложке clouds: CLOUDS_OFF; птиц — birds: BIRDS_OFF.
 *
 * Ключи — только существующие подложки: ссылка на снятую с раздачи
 * (закомментированную в GROUND_BACKGROUNDS) превращается в ключ undefined
 */
const AMBIENCE_BY_GROUND_BACKGROUND = Object.freeze({
  // Зеленые равнины, остров, плато
  [GROUND_BACKGROUNDS.GREEN_1]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.GREEN_2]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.GREEN_3]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.GREEN_4]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.ISLAND_1]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.PLATO_1]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE_OVER_DARK},
  [GROUND_BACKGROUNDS.RICE_1]: {birds: BIRDS_ANY, clouds: CLOUDS_WHITE},
  // Снег, лед, пар гейзеров: белых птиц там не разглядеть. Белое облако на
  // снегу тоже почти не видно — зато видна его тень
  [GROUND_BACKGROUNDS.WINTER_1]: {birds: BIRDS_DARK, clouds: CLOUDS_WHITE},
  [GROUND_BACKGROUNDS.WINTER_2]: {birds: BIRDS_DARK, clouds: CLOUDS_WHITE},
  [GROUND_BACKGROUNDS.WINTER_3]: {birds: BIRDS_DARK, clouds: CLOUDS_WHITE},
  [GROUND_BACKGROUNDS.WINTER_4]: {birds: BIRDS_DARK, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.WINTER_5]: {birds: BIRDS_DARK, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.GEYSER_1]: {birds: BIRDS_DARK, clouds: CLOUDS_WHITE},
  // Солнечные подложки: ясное небо, облаков нет. На белой соли нет и белых птиц
  [GROUND_BACKGROUNDS.DESERT_1]: {birds: BIRDS_ANY, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.DESERT_2]: {birds: BIRDS_ANY, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.RED_1]: {birds: BIRDS_ANY, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.BONE_1]: {birds: BIRDS_ANY, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.SPIRAL_1]: {birds: BIRDS_ANY, clouds: CLOUDS_OFF},
  [GROUND_BACKGROUNDS.SALT_1]: {birds: BIRDS_DARK, clouds: CLOUDS_OFF},
  // Темные подложки: вместо белых облаков — дымка в тон; черных и коричневых
  // птиц на них не разглядеть
  [GROUND_BACKGROUNDS.ASH_1]: {birds: BIRDS_WHITE, clouds: createHaze(0x9a9a94, 0.5)},
  // На картинке вулкана дым из кратера тянется вправо, и живой дым идет по нему
  // же (эффект подложки craterPlume): ветер здесь всегда слева направо, иначе
  // в половине партий дымка плыла бы навстречу дыму
  [GROUND_BACKGROUNDS.VOLCANO_1]: {birds: BIRDS_WHITE, clouds: createHaze(0xb0b0b0, 0.5), windDirection: 1},
  [GROUND_BACKGROUNDS.BOG_1]: {birds: BIRDS_WHITE, clouds: createHaze(0xb4c2b4, 0.4)},
  [GROUND_BACKGROUNDS.MUSHROOM_1]: {birds: BIRDS_WHITE, clouds: createHaze(0xb9b4d8, 0.4)},
  [GROUND_BACKGROUNDS.MAGICAL_FOREST_2]: {birds: BIRDS_WHITE, clouds: createHaze(0xc4d6ee, 0.45)},
  // Пекло: над лавой птицы не летают, вместо облаков — бурый дым
  [GROUND_BACKGROUNDS.DEMON_1]: {birds: BIRDS_OFF, clouds: createHaze(0x8a7268, 0.55)},
});

/**
 * Антураж для подложки поля
 * @param {string|null} [groundBackgroundId] — идентификатор подложки
 * @returns {{birds: Object, clouds: Object, windDirection: (number|undefined)}}
 *   birds: {isEnabled, colors}; clouds: {isEnabled, tint, alphaFactor,
 *   hasShadow, shadowAlphaFactor, isThinnerOverField}; windDirection — куда на
 *   этой подложке дует
 *   ветер всегда (1 — слева направо, -1 — справа налево); не задано — по
 *   партии. Для неизвестной подложки — антураж по умолчанию
 */
export const getFieldAmbience = (groundBackgroundId) => (
  // Только свои ключи: подложка приходит и из файла состояния, а имя вроде
  // «constructor» нашлось бы в прототипе объекта
  Object.prototype.hasOwnProperty.call(AMBIENCE_BY_GROUND_BACKGROUND, groundBackgroundId)
    ? AMBIENCE_BY_GROUND_BACKGROUND[groundBackgroundId]
    : DEFAULT_AMBIENCE
);

/** Подложки, которым антураж назначен поименно (для проверок перечня) */
export const GROUND_BACKGROUNDS_WITH_AMBIENCE = Object.freeze(Object.keys(AMBIENCE_BY_GROUND_BACKGROUND));
